import { useMemo } from 'react';

/**
 * A threshold to determine when to switch from simple filtering to a more
 * performant Map-based lookup. This is for optimizing performance with
 * very large option lists. This value can be adjusted based on real-world
 * performance testing.
 */
const LARGE_OPTIONS_THRESHOLD = 1000;

/**
 * A custom React hook that transforms a given value into a consistent format
 * (either a single option object or an array of option objects) based on a
 * list of available options.
 *
 * It's designed to handle various input formats and is optimized to perform
 * well with small, medium, and large datasets by dynamically switching its
 * lookup strategy.
 *
 * @param {string|number|Array<string|number>|object|Array<object>|null} value The input value to transform.
 * @param {Array<{value: any, label: string}>} options The list of available options to match against.
 * @param {boolean} isMulti - If true, the hook returns an array of matching options. If false, it returns the first matching option or null.
 * @returns {object|Array<object>|null} The transformed value in the desired option format.
 */
export const useTransformedValue = (value, options, isMulti) => useMemo(() => {
  // If the value is null, undefined, or an empty string, return the
  // appropriate empty state immediately.
  if (value === null || value === undefined || value === '') {
    return isMulti ? [] : null;
  }

  // If the value is already in the correct object format, return it directly
  // to avoid unnecessary processing. This is an optimization.
  if (typeof value === 'object') {
    if (isMulti && Array.isArray(value) && value.every((v) => v && typeof v === 'object' && 'value' in v)) {
      return value;
    }
    if (!isMulti && !Array.isArray(value) && 'value' in value) {
      return value;
    }
  }

  // Convert the various possible input types into a standardized array of values.
  let valuesToFind = [];
  if (typeof value === 'string') {
    valuesToFind = value.split(',').map((v) => v.trim()).filter(Boolean);
  } else if (Array.isArray(value)) {
    valuesToFind = value;
  } else {
    valuesToFind = [value];
  }

  if (valuesToFind.length === 0) {
    return isMulti ? [] : null;
  }

  let results;
  const useMapStrategy = options.length >= LARGE_OPTIONS_THRESHOLD;

  if (useMapStrategy) {
    const optionsMap = new Map(options.map((opt) => [String(opt.value), opt]));
    results = valuesToFind
      .map((v) => optionsMap.get(String(v)))
      .filter(Boolean);
  } else {
    const lookupSet = new Set(valuesToFind.map(String));
    results = options.filter((opt) => lookupSet.has(String(opt.value)));
  }

  // Return the full array of results for multi-select, or the first
  // result (or null if no match was found) for single-select.
  return isMulti ? results : results[0] || null;
}, [value, options, isMulti]);
